import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A vertical grouped bar chart is a data visualization tool that displays multiple
          categories side by side within each group using vertical bars. Each group represents a
          specific category or time period, and the bars within the group compare subcategories or
          variables. This chart type is ideal for visualizing relationships, comparing multiple data
          series, and analyzing trends across different groups simultaneously.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Vertical grouped bar charts are ideal for</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Comparing Multiple Categories Across Different Groups:</strong> When you need to
            compare multiple variables within different categories, such as sales performance across
            regions for various product types, a vertical grouped bar chart provides a clear visual
            distinction.
          </li>
          <li>
            <strong>Tracking Trends for Multiple Groups:</strong> It helps visualize trends for
            different groups over time or categories, such as comparing yearly revenue for different
            product lines or departments.
          </li>
          <li>
            <strong>Visualizing Subcategory Relationships:</strong> This chart type allows for an
            easy comparison of subcategories within larger categories, such as analyzing the
            performance of different marketing campaigns within each quarter.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Vertical grouped bar charts are a useful data visualization tool, but they come with some
          limitations that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Cluttered Display with Many Groups:</strong> When there are too many groups or
            categories, vertical grouped bar charts can become cluttered and difficult to read,
            especially if the bars are too close together.
          </li>
          <li>
            <strong>Difficulty with Precise Comparisons:</strong> It can be challenging to
            accurately compare the heights of bars within each group, especially when the
            differences are small, which may lead to misinterpretation.
          </li>
          <li>
            <strong>Limited Space for Labels and Legends:</strong> In cases where there are many
            groups or sub-categories, there may not be enough space to display labels or legends
            clearly, reducing the chart&apos;s readability and effectiveness.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is essential in vertical grouped bar charts to clearly distinguish between
          data groups and subcategories. Using a consistent color scheme for each group, with
          variations in shades or patterns for subcategories, ensures that comparisons are
          straightforward and intuitive. High-contrast colors make overlapping or adjacent bars easy
          to differentiate, while subtle tones can highlight secondary data without dominating the
          chart. Thoughtful color choices enhance readability, reduce visual clutter, and
          effectively convey complex data relationships.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Vertical grouped bar charts are a powerful visualization tool, but they come with
          challenges that should be taken into account:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Cluttered Appearance:</strong> When there are many categories or groups,
            vertical grouped bar charts can become cluttered, making it harder to interpret data
            clearly, especially if the bars are too close together.
          </li>
          <li>
            <strong>Difficulty in Comparing Groups:</strong> It can be challenging to compare the
            size of different groups across categories, particularly when bars within a group vary
            significantly in length, making visual comparison harder.
          </li>
          <li>
            <strong>Limited Space for Long Labels:</strong> Vertical grouped bar charts can struggle
            to display long category or group labels effectively, especially when there are multiple
            groups within each category, leading to overlapping or truncated text.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
